import type { DeepPartial, TabProps, TabsState } from '@talos/kyoko';
import { cloneDeep } from 'lodash-es';
import type { AppConfigStateWithLegacyLayoutConfigs } from './legacyLayoutConfigs.types';

/** Minimal panel shape used when walking trading layouts during tab migrations. */
export interface DockviewPanelLike {
  contentComponent?: string;
  params?: { type?: string; tabs?: unknown };
  tabComponent?: string;
}

/** Inputs for blotter tab migrations: `spacesConfigs` and/or legacy `layoutConfigs`. */
export type BlotterTabsLayoutConfigInput = DeepPartial<
  Pick<AppConfigStateWithLegacyLayoutConfigs, 'spacesConfigs' | 'layoutConfigs'>
>;

/**
 * During `replaceLegacyConfig`, trading layouts usually live under `layoutConfigs.trading` (dict or array).
 * After migration, they live under `spacesConfigs.trading.layouts`. Prefer legacy when it has entries.
 */
export function forEachTradingLayoutSpecForTabsMigration(
  config: BlotterTabsLayoutConfigInput,
  visitor: (layoutConfig: { dockViewLayout?: { panels?: Record<string, DockviewPanelLike> } }) => void
): void {
  const legacyTrading = config.layoutConfigs?.trading;

  if (legacyTrading != null) {
    const specs: unknown[] = Array.isArray(legacyTrading)
      ? legacyTrading
      : Object.values(legacyTrading as Record<string, unknown>);
    if (specs.length > 0) {
      for (const layoutConfig of specs) {
        if (layoutConfig != null && typeof layoutConfig === 'object') {
          visitor(layoutConfig as { dockViewLayout?: { panels?: Record<string, DockviewPanelLike> } });
        }
      }
      return;
    }
  }

  for (const layoutConfig of config.spacesConfigs?.trading?.layouts ?? []) {
    if (layoutConfig != null && typeof layoutConfig === 'object') {
      visitor(layoutConfig as { dockViewLayout?: { panels?: Record<string, DockviewPanelLike> } });
    }
  }
}

export function isTradingBlotterPanel(panel: DockviewPanelLike, blotterType: string): boolean {
  return panel.contentComponent === blotterType || panel.params?.type === blotterType;
}

/** Clones persisted tab state, preserving all tab ids so they match up with app config filters. */
export function cloneTabsState<T extends TabProps>(legacyTabs: TabsState<T>): TabsState<T> {
  return cloneDeep(legacyTabs);
}

/**
 * Copies legacy app-wide blotter tab state into each matching trading layout panel.
 * Gating is via `blotterTabsMigrationVersion` on app config
 * (see `shouldRunBlotterTabsMigration` / `migrateBlotterTabsToPanelParams`).
 */
export function migrateLegacyTabsIntoTradingBlotterPanels<T extends TabProps>(
  config: BlotterTabsLayoutConfigInput,
  options: {
    legacyTabs: TabsState<T> | undefined | null;
    blotterType: string;
  }
): boolean {
  const { legacyTabs, blotterType } = options;
  if (legacyTabs == null) {
    return false;
  }

  let foundMatchingPanel = false;

  forEachTradingLayoutSpecForTabsMigration(config, layoutConfig => {
    const panels = layoutConfig?.dockViewLayout?.panels;
    if (panels == null) {
      return;
    }

    for (const panel of Object.values(panels)) {
      if (panel == null) {
        continue;
      }

      if (!isTradingBlotterPanel(panel, blotterType)) {
        continue;
      }

      foundMatchingPanel = true;

      panel.params = {
        ...(panel.params ?? {}),
        type: blotterType,
        tabs: cloneTabsState(legacyTabs),
      };
    }
  });

  return foundMatchingPanel;
}
